import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { 
  Table, 
  Badge, 
  Spinner, 
  Card, 
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  ProgressBar,
  Stack,
  Dropdown,
  Modal
} from 'react-bootstrap';
import { 
  Clock, 
  Calendar, 
  CurrencyDollar, 
  Person, 
  Phone, 
  Envelope,
  Search,
  Funnel,
  CheckCircle,
  XCircle,
  ClockHistory,
  Infinity as InfiniteIcon,
  Eye,
  ThreeDotsVertical,
  ArrowLeft,
  FileEarmarkSpreadsheet
} from 'react-bootstrap-icons';
import moment from 'moment';
import { parse, format } from 'date-fns';
import DatePicker from "react-datepicker";
import { useNavigate } from 'react-router-dom';

const SubscriptionUsers = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filter, setFilter] = useState("all");
    const [pagination, setPagination] = useState({
        currentPage: 1,
        itemsPerPage: 25,
        totalPages: 1,
        totalRecords: 0
    });
    const navigate = useNavigate();
    const [touched, setTouched] = useState(false);
    
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        pending: 0,
        hourly: 0,
        daily: 0,
        monthly: 0,
        yearly: 0
    });

    const accessToken = localStorage.getItem("authToken");
    const [subscriptionPurchaseList, setSubscriptionPurchaseList] = useState([]);
    const [selectedSubType, setSelectedSubType] = useState('ALL');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');
    const [singleSubscriptionData, setSingleSubscriptionData] = useState({});
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [clicked, setClicked] = useState(false);

    const formatDate = (dateStr) => {
        const parsed = parse(dateStr, 'd/M/yyyy, hh:mm:ss a', new Date())
        return format(parsed, 'dd MMM yyyy')
    }

    const getSubscriptionPurhcases = async (start, end, page, limit, type = '', status = '', search = '') => {
        try {
            setLoading(true);
            const headers = {
                Authorization: `Bearer ${accessToken}` 
            };
            
            const updatedStartDate = start ? start.toISOString().split("T")[0] : null;
            const updatedEndDate = end ? end.toISOString().split("T")[0] : null;
            
            let url = `subscription-purchase?startDate=${updatedStartDate}&endDate=${updatedEndDate}&page=${page}&limit=${limit}`;
            
            // Add filters if they exist
            if (type && type !== 'ALL') {
                url += `&subscriptionType=${type}`;
            }
            
            if (status && status !== 'all') {
                url += `&status=${status}`;
            }
            
            if (search) {
                url += `&search=${search}`;
            }

            const response = await axios.get(url, { headers });
            const data = response?.data?.data?.list || [];
            console.log("List of purchases :", response?.data?.data);
            
            
            setSubscriptionPurchaseList(data);
            setStats({
                total: response?.data?.data?.summary?.totalRecords,
                active: response?.data?.data?.summary?.active,
                expired: response?.data?.data?.summary?.expired,
                hourly: response?.data?.data?.summary?.subscriptionTypeBreakdown[0]?.count,
                daily: response?.data?.data?.summary?.subscriptionTypeBreakdown[1]?.count
            })
            
            // Update pagination from server response
            setPagination(prev => ({
                ...prev,
                totalPages: response?.data?.data?.pagination?.totalPages || 1,
                totalRecords: response?.data?.data?.pagination?.totalRecords || 0
            }));
            
            // Calculate stats from server response if available
            // Or calculate client-side if not provided by server
            // setStats({
            //     total: response?.data?.data?.pagination?.totalRecords || 0,
            //     active: data.filter(s => s.validity.status === "ACTIVE").length,
            //     expired: data.filter(s => s.validity.status === "EXPIRED").length,
            //     pending: data.filter(s => s.validity.status === "PENDING").length,
            //     hourly: data.filter(s => s.subscriptionType === "HOURLY").length,
            //     daily: data.filter(s => s.subscriptionType === "DAILY").length,
            //     monthly: data.filter(s => s.subscriptionType === "MONTHLY").length,
            //     yearly: data.filter(s => s.subscriptionType === "YEARLY").length
            // });
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to fetch subscription purchases", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (touched) {
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getSubscriptionPurhcases(adjustedStartDate, adjustedEndDate, pagination.currentPage, pagination.itemsPerPage, selectedSubType, filter, searchTerm)    
        }
    }, [ filter ]);

    const handleEndDateChange = (date) => {
        if (date && startDate && date < startDate) {
            setError('End date cannot be earlier than start date');
        } else {
            setError('');
            setEndDate(date);
        }
    };

    const handlePageChange = (pageNumber) => {
        setPagination(prev => ({ ...prev, currentPage: pageNumber }));
        getSubscriptionPurhcases(
            startDate, 
            endDate, 
            pageNumber, 
            pagination.itemsPerPage, 
            selectedSubType, 
            filter,
            searchTerm
        );
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setPagination(prev => ({ 
            ...prev, 
            itemsPerPage: newItemsPerPage,
            currentPage: 1 // Reset to first page when changing items per page
        }));
        getSubscriptionPurhcases(
            startDate, 
            endDate, 
            1, 
            newItemsPerPage, 
            selectedSubType, 
            filter,
            searchTerm
        );
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "ACTIVE":
                return <Badge bg="success" className="d-flex align-items-center"><CheckCircle size={14} className="me-1" /> Active</Badge>;
            case "EXPIRED":
                return <Badge bg="danger" className="d-flex align-items-center"><XCircle size={14} className="me-1" /> Expired</Badge>;
            case "PENDING":
                return <Badge bg="warning" className="d-flex align-items-center"><ClockHistory size={14} className="me-1" /> Pending</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const getSubscriptionTypeBadge = (type) => {
        switch (type) {
            case "HOURLY":
                return <Badge bg="info" className="text-capitalize"><Clock className="me-1" /> Hourly</Badge>;
            case "DAILY":
                return <Badge bg="primary" className="text-capitalize"><Calendar className="me-1" /> Daily</Badge>;
            case "MONTHLY":
                return <Badge bg="purple" className="text-capitalize"><Calendar className="me-1" /> Monthly</Badge>;
            case "YEARLY":
                return <Badge bg="dark" className="text-capitalize"><Calendar className="me-1" /> Yearly</Badge>;
            default:
                return <Badge bg="secondary" className="text-capitalize"><CurrencyDollar className="me-1" /> {type.toLowerCase()}</Badge>;
        }
    };

    const getValidityProgress = (startDate, endDate) => {
        const start = moment(startDate);
        const end = moment(endDate);
        const now = moment();
        
        if (now.isAfter(end)) return 100;
        if (now.isBefore(start)) return 0;
        
        const totalDuration = end.diff(start);
        const elapsedDuration = now.diff(start);
        return Math.round((elapsedDuration / totalDuration) * 100);
    };

    const handleViewDetails = (data) => {
        console.log("View Modal Data :", data);
        setSingleSubscriptionData(data);
        setShowDetailsModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        await getSubscriptionPurhcases(
            adjustedStartDate, 
            adjustedEndDate, 
            1, 
            pagination.itemsPerPage, 
            selectedSubType,
            filter,
            searchTerm
        );
    };

    const formatDateTime = (dateStr) => {
        try {
            // Parse the date string (format: "d/M/yyyy, hh:mm:ss a")
            const parsed = parse(dateStr, 'd/M/yyyy, hh:mm:ss a', new Date());
            
            // Format as "dd MMM yyyy, hh:mm a" (e.g., "02 Jul 2025, 06:58 PM")
            return format(parsed, 'dd MMM yyyy, hh:mm a');
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateStr; // Return original string if formatting fails
        }
    };

    const handleDownloadReport = async () => {
        try {
            setLoading(true);
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            };

            const updatedStartDate = startDate ? startDate.toISOString().split("T")[0] : null;
            const updatedEndDate = endDate ? endDate.toISOString().split("T")[0] : null;

            let url = `subscription-purchase?startDate=${updatedStartDate}&endDate=${updatedEndDate}&isDownload=true`;

            // Add filters if they exist
            if (selectedSubType && selectedSubType !== 'ALL') {
                url += `&subscriptionType=${selectedSubType}`;
            }

            if (filter && filter !== 'all') {
                url += `&status=${filter}`;
            }

            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }

            const response = await axios.get(url, { 
                headers,
                responseType: 'blob' // Important for file downloads
            });

            // Create a blob from the response data
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });

            // Create a link element
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            
            // Set the filename for the download
            const fileName = `Subscription Purchase Report (${updatedStartDate} - ${updatedEndDate}).xlsx`;
            link.download = fileName;
            
            // Append the link to the body and trigger the download
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);

            toast.success("Report downloaded successfully!", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } catch (error) {
            console.error("Download error:", error);
            toast.error(error.response?.data?.error || "Failed to download report", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate index for display
    const indexOfFirstItem = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const indexOfLastItem = Math.min(indexOfFirstItem + pagination.itemsPerPage, pagination.totalRecords);

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        
        setStartDate(start);
        setEndDate(end);

        getSubscriptionPurhcases(start, end, 1, pagination.itemsPerPage);
    }, []);

    useEffect(() => {
        console.log("clicked :", clicked);
        
        if (searchTerm === "" && clicked === true) {
            console.log("clicked inside if statement :", clicked);
            getSubscriptionPurhcases(startDate, endDate, pagination?.currentPage, pagination?.itemsPerPage, selectedSubType);
        }
    }, [searchTerm])


    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "dark-mode" : ""}`}>
                <Container className='shadow-sm bg-white' fluid>
                   <Card.Header className="bg-white border-0 py-2 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-semibold d-inline-block">
                                <i className="bi bi-credit-card me-2"></i>SUBSCRIPTION PURCHASE REPORT
                            </h4>
                            {/* Optional subtitle - uncomment if needed */}
                            {/* <p className="text-muted mb-0 mt-1">(View and manage all user subscriptions)</p> */}
                        </div>
                        <div>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </Card.Header>
                    <Row>
                        <hr />
                    </Row>

                    {/* Stats Cards */}
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={4}>
                            <Card className="h-100 card-hover">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-uppercase text-muted fs-xs mb-2">Total Subscriptions</h6>
                                            <h3 className="mb-0">{stats.total}</h3>
                                        </div>
                                        <div className="bg-primary bg-opacity-10 p-3 rounded">
                                            <Person size={24} className="text-primary" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={6} md={4}>
                            <Card className="h-100 card-hover">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-uppercase text-muted fs-xs mb-2">Active</h6>
                                            <h3 className="mb-0">{stats.active}</h3>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-3 rounded">
                                            <CheckCircle size={24} className="text-success" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={6} md={4}>
                            <Card className="h-100 card-hover">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-uppercase text-muted fs-xs mb-2">Expired</h6>
                                            <h3 className="mb-0">{stats.expired}</h3>
                                        </div>
                                        <div className="bg-danger bg-opacity-10 p-3 rounded">
                                            <XCircle size={24} className="text-danger" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <hr />
                    </Row>

                    <Card className="border-0 shadow-sm p-1 mb-3">
                        <Row>
                            <Col md={2}>
                                <Form.Group controlId="startDate">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-calendar me-2"></i>Start Date
                                    </Form.Label>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="Start Date"
                                        className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group controlId="endDate">
                                <Form.Label className="fw-semibold">
                                    <i className="bi bi-calendar me-2"></i>End Date
                                </Form.Label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleEndDateChange}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="End Date"
                                    className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                    minDate={startDate}
                                />
                                </Form.Group>
                            </Col>
                            <Col md="2">
                                <Form.Group controlId="accountSelect">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-funnel me-2"></i>Subscription Type
                                    </Form.Label>
                                    <Form.Select 
                                        value={selectedSubType}
                                        onChange={(e) => setSelectedSubType(e.target.value)}
                                        className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                    >
                                        <option value="" disabled>Select</option>
                                        <option value="ALL">ALL</option>
                                        <option value="HOURLY">HOURLY</option>
                                        <option value="DAILY">DAILY</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md="4">
                                <Form.Label className="fw-semibold">
                                    <i className="bi bi-search me-2"></i>Search
                                </Form.Label>
                                <div className="input-group w-100">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setClicked(true);
                                        }}
                                        placeholder="Search for username, mobile, email ..."
                                    />
                                    <button className="btn btn-primary" type="button">
                                        <i className="ri-search-line"></i>
                                    </button>
                                </div>
                            </Col>
                            <Col md={2}>
                                <Form.Label className="text-white">
                                    .
                                </Form.Label>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="w-100"
                                    onClick={handleSubmit}
                                >
                                    {loading ? (
                                        <Spinner as="span" animation="border" size="sm" />
                                    ) : (
                                        "Submit"
                                    )}
                                </Button>
                            </Col>
                        </Row>
                        <Row className='mt-3' style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Col md="6" className='text-start'>
                                <ButtonGroup>
                                    <Button 
                                        variant={filter === "all" ? "primary" : "outline-secondary"}
                                        onClick={() => {
                                                setFilter("all");
                                                setTouched(true);
                                            }
                                        }
                                    >
                                        All
                                    </Button>
                                    <Button 
                                        variant={filter === "ACTIVE" ? "primary" : "outline-secondary"}
                                        onClick={() => {
                                            setTouched(true);
                                            setFilter("ACTIVE");
                                            }
                                        }
                                    >
                                        Active
                                    </Button>
                                    <Button 
                                        variant={filter === "EXPIRED" ? "primary" : "outline-secondary"}
                                        onClick={() => {
                                            setFilter("EXPIRED")
                                            setTouched(true);
                                        }}
                                    >
                                        Expired
                                    </Button>
                                </ButtonGroup>
                            </Col>
                            <Col md={2} >
                                <Button
                                    variant="outline-success"
                                    className="w-100 fw-semibold"
                                    onClick={handleDownloadReport}
                                    disabled={loading}
                                >
                                    <FileEarmarkSpreadsheet className='me-2' />
                                    {loading ? (
                                        <Spinner as="span" animation="border" size="sm" />
                                    ) : (
                                        "Download Report"
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                    {loading ? (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <h5 className="mt-3">Loading subscription data...</h5>
                                <p className="text-muted">Please wait while we fetch the latest information</p>
                            </Card.Body>
                        </Card>
                    ) : subscriptionPurchaseList?.length === 0 ? (
                        <>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="text-center py-5">
                                    <div className="bg-light rounded-circle d-inline-flex p-3 mb-3">
                                        <Funnel size={32} className="text-muted" />
                                    </div>
                                    <h4>No subscriptions found</h4>
                                    <p className="text-muted mb-4">
                                        {searchTerm || filter !== "all" 
                                            ? "No results match your search criteria. Try adjusting your filters." 
                                            : "There are currently no subscription records"}
                                    </p>
                                    {searchTerm || filter !== "all" ? (
                                        <Button variant="outline-primary" onClick={() => {
                                            setSearchTerm("");
                                            setFilter("all");
                                        }}>
                                            Clear filters
                                        </Button>
                                    ) : null}
                                </Card.Body>
                            </Card>
                        </>
                    ) : (
                        <div className='pb-3'>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-0 py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">USERS LIST</h5>
                                        <div className="text-muted">
                                            Showing <strong>{indexOfFirstItem + 1}-{indexOfLastItem}</strong> of <strong>{pagination.totalRecords}</strong> subscriptions
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Sr.no</th>
                                                    <th className="ps-4">User</th>
                                                    <th>Subscription</th>
                                                    <th>Validity Period</th>
                                                    <th>Status</th>
                                                    <th>Order Count</th>
                                                    <th>Cancelled Orders</th>
                                                    <th>Remaining Count</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subscriptionPurchaseList?.map((subscription, index) => (
                                                    <tr key={subscription?._id} className="align-middle">
                                                        <td>
                                                            {indexOfFirstItem + index + 1}
                                                        </td>
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar bg-primary bg-opacity-10 text-primary rounded-circle me-3 d-flex align-items-center justify-content-center fw-bold" 
                                                                    style={{ width: '40px', height: '40px' }}>
                                                                    {subscription?.user?.name?.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1">{subscription?.user?.name}</h6>
                                                                    <div className="text-muted small d-flex align-items-center">
                                                                        <Envelope size={12} className="me-1" />
                                                                        {subscription?.user?.email}
                                                                    </div>
                                                                    <div className="text-muted small d-flex align-items-center">
                                                                        <Phone size={12} className="me-1" />
                                                                        {subscription?.user?.mobile}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className='fw-semibold text-success'>{subscription?.title}</span>
                                                                {getSubscriptionTypeBadge(subscription.subscriptionType)}
                                                                {subscription.grandTotal !== null && (
                                                                    <>
                                                                        <div className="mt-1 small fw-semibold">
                                                                            ₹. {subscription.grandTotal.toFixed(2)}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column small">
                                                                <div className="d-flex align-items-center text-muted">
                                                                    <Calendar size={12} className="me-1" />
                                                                    {subscription?.validity?.startDate}
                                                                </div>
                                                                <div className="d-flex align-items-center text-muted">
                                                                    <Calendar size={12} className="me-1" />
                                                                    {subscription?.validity?.endDate}
                                                                </div>
                                                                <div className="mt-2">
                                                                    <ProgressBar 
                                                                        now={getValidityProgress(subscription.validity.startDate, subscription.validity.endDate)}
                                                                        variant={subscription.validity.status === "ACTIVE" ? "success" : "danger"}
                                                                        className="rounded-pill" 
                                                                        style={{ height: '4px' }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {getStatusBadge(subscription.validity.status)}
                                                        </td>
                                                        <td className='text-center'>
                                                            {subscription?.orderCount}
                                                        </td>
                                                        <td className='text-center text-danger'>
                                                            {subscription?.cancelledOrderCount ? subscription?.cancelledOrderCount : '0'}
                                                        </td>
                                                        <td className='text-center'>
                                                            {subscription?.remainingOrders ? subscription?.remainingOrders : 'UNLIMITED'}
                                                        </td>
                                                        {/* <td className="pe-4">
                                                            {subscription.orderLimit ? (
                                                                <div className="d-flex flex-column">
                                                                    <div className="d-flex justify-content-between small mb-1">
                                                                        <span className="text-muted">Orders:</span>
                                                                        <span>{subscription.allowedOrderQuantity}/{subscription.orderLimit}</span>
                                                                    </div>
                                                                    <ProgressBar 
                                                                        now={(subscription.allowedOrderQuantity / subscription.orderLimit) * 100}
                                                                        variant="info"
                                                                        className="rounded-pill" 
                                                                        style={{ height: '4px' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <Badge bg="light" text="dark" className="d-flex align-items-center">
                                                                    <InfiniteIcon size={14} className="me-1" />
                                                                    Unlimited
                                                                </Badge>
                                                            )}
                                                        </td> */}
                                                        <td>
                                                            <Dropdown>
                                                                <Dropdown.Toggle variant="light" id="dropdown-basic" size="sm">
                                                                    <ThreeDotsVertical size={15} />
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu>
                                                                    <Dropdown.Item onClick={() => handleViewDetails(subscription)}>
                                                                        <Eye size={14} className="me-2" /> View
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                                <Card.Footer className="bg-white border-0 py-3">
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                                        <div className="text-muted small mb-2 mb-md-0">
                                            Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{indexOfLastItem}</strong> of <strong>{pagination.totalRecords}</strong> entries
                                        </div>
                                        
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Select 
                                                size="sm" 
                                                style={{ width: '80px' }}
                                                value={pagination.itemsPerPage}
                                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                            >
                                                <option value="5">5</option>
                                                <option value="10">10</option>
                                                <option value="25">25</option>
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                            </Form.Select>
                                            
                                            <ButtonGroup size="sm">
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={pagination.currentPage === 1}
                                                >
                                                    Previous
                                                </Button>
                                                
                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNumber;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.currentPage <= 3) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                                        pageNumber = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNumber = pagination.currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <Button 
                                                            key={pageNumber}
                                                            variant={pagination.currentPage === pageNumber ? "primary" : "outline-secondary"}
                                                            onClick={() => handlePageChange(pageNumber)}
                                                        >
                                                            {pageNumber}
                                                        </Button>
                                                    );
                                                })}
                                                
                                                {pagination.totalPages > 5 && pagination.currentPage < pagination.totalPages - 2 && (
                                                    <Button variant="outline-secondary" disabled>...</Button>
                                                )}
                                                
                                                {pagination.totalPages > 5 && pagination.currentPage < pagination.totalPages - 2 && (
                                                    <Button 
                                                        variant={pagination.currentPage === pagination.totalPages ? "primary" : "outline-secondary"}
                                                        onClick={() => handlePageChange(pagination.totalPages)}
                                                    >
                                                        {pagination.totalPages}
                                                    </Button>
                                                )}
                                                
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                                                >
                                                    Next
                                                </Button>
                                            </ButtonGroup>
                                        </div>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </div>
                    )}

                    {
                        showDetailsModal && (
                            <Modal 
                                show={showDetailsModal} 
                                onHide={() => setShowDetailsModal(false)}
                                size="xl" // Extra large modal for better space
                                centered
                                className="subscription-details-modal"
                            >
                            <Modal.Header closeButton className="border-bottom-0">
                                <Modal.Title className="fw-bold">Subscription Details</Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="p-0">
                                {singleSubscriptionData && (
                                <div className="container-fluid h-100">
                                    <div className="row g-0 h-100">
                                        {/* Left Column - Subscription Info */}
                                        <div className="col-lg-4 p-4 border-end" style={{ backgroundColor: '#f8f9fa' }}>
                                            <div className="sticky-top" style={{ top: '1rem' }}>
                                                <div className="mb-4">
                                                    <h5 className="fw-bold mb-3">Subscription Information</h5>
                                                    <div className="card border-0 shadow-sm mb-3">
                                                    <div className="card-body">
                                                        <div className="mb-3">
                                                        <h6 className="text-muted small mb-2">Subscription Type</h6>
                                                        <div>{getSubscriptionTypeBadge(singleSubscriptionData.subscriptionType)}</div>
                                                        </div>
                                                        
                                                        <div className="mb-3">
                                                        <h6 className="text-muted small mb-2">Validity Period</h6>
                                                        <div className="d-flex align-items-center text-muted small mb-1">
                                                            <Calendar size={14} className="me-2" />
                                                            {singleSubscriptionData?.validity?.startDate} to {singleSubscriptionData?.validity?.endDate}
                                                        </div>
                                                        <ProgressBar 
                                                            now={getValidityProgress(
                                                            singleSubscriptionData?.validity?.startDate, 
                                                            singleSubscriptionData?.validity?.endDate
                                                            )}
                                                            variant={singleSubscriptionData?.validity?.status === "ACTIVE" ? "success" : "danger"}
                                                            className="rounded-pill" 
                                                            style={{ height: '6px' }}
                                                        />
                                                        </div>
                                                        
                                                        <div className="mb-3">
                                                        <h6 className="text-muted small mb-2">Status</h6>
                                                        <div>{getStatusBadge(singleSubscriptionData.validity.status)}</div>
                                                        </div>
                                                    </div>
                                                    </div>

                                                    <div className="card border-0 shadow-sm mb-3">
                                                        <div className="card-body">
                                                            <h6 className="text-muted small mb-2">Payment Details</h6>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Rate:</span>
                                                                <span>₹{singleSubscriptionData.rate?.toFixed(2)}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Tax:</span>
                                                                <span>₹{singleSubscriptionData.taxAmount?.toFixed(2)}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between fw-bold">
                                                                <span>Total:</span>
                                                                <span>₹{singleSubscriptionData.grandTotal?.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="card border-0 shadow-sm">
                                                        <div className="card-body">
                                                            <h6 className="text-muted small mb-2">Order Limits</h6>
                                                            {singleSubscriptionData?.orderLimit ? (
                                                                <>
                                                                    <div className="d-flex justify-content-between mb-1">
                                                                        <span>Allowed:</span>
                                                                        <span>{singleSubscriptionData.orderLimit}</span>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between mb-1">
                                                                        <span>Used:</span>
                                                                        <span>{singleSubscriptionData.orderCount}</span>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between fw-bold">
                                                                        <span>Remaining:</span>
                                                                        <span>{singleSubscriptionData.remainingOrders}</span>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <Badge bg="light" text="dark" className="d-flex align-items-center w-100 justify-content-center py-2">
                                                                    <InfiniteIcon size={14} className="me-1" />
                                                                    Unlimited Orders
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <h5 className="fw-bold mb-3">User Information</h5>
                                                    <div className="card border-0 shadow-sm">
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar bg-primary bg-opacity-10 text-primary rounded-circle me-3 d-flex align-items-center justify-content-center fw-bold" 
                                                                    style={{ width: '50px', height: '50px' }}>
                                                                    {singleSubscriptionData?.user?.name?.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1 fw-bold">{singleSubscriptionData?.user?.name}</h6>
                                                                    <div className="text-muted small d-flex align-items-center mb-1">
                                                                    <Envelope size={12} className="me-1" />
                                                                    {singleSubscriptionData?.user?.email}
                                                                    </div>
                                                                    <div className="text-muted small d-flex align-items-center">
                                                                    <Phone size={12} className="me-1" />
                                                                    {singleSubscriptionData?.user?.mobile}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column - Order History */}
                                        <div className="col-lg-8 p-4">
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                <h5 className="fw-bold mb-0">Order History</h5>
                                                <Badge bg="secondary" pill className="fs-6 py-2 px-3">
                                                    {singleSubscriptionData?.orders?.length || 0} orders
                                                </Badge>
                                            </div>

                                            {singleSubscriptionData?.orders?.length > 0 ? (
                                                <div 
                                                    className="order-list-container" 
                                                    style={{ 
                                                    maxHeight: 'calc(100vh - 200px)', 
                                                    overflowY: 'auto',
                                                    paddingRight: '10px'
                                                    }}
                                                >
                                                    {singleSubscriptionData.orders.map((order, index) => (
                                                    <div 
                                                        key={order._id} 
                                                        className="card border-0 shadow-sm mb-3"
                                                    >
                                                        <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                            <h6 className="fw-bold mb-1">Order #A{order?.order_no}</h6>
                                                                <div className="d-flex align-items-center">
                                                                    <small className="text-muted me-2">
                                                                        <Calendar size={12} className="me-1" />
                                                                        {formatDateTime(order?.createdAt)}
                                                                    </small>
                                                                    <Badge 
                                                                        bg={order?.orderStatus === "warning" ? "primary" : order?.orderStatus === "DELIVERED" ? "success" : order?.orderStatus === "CANCELLED" ? "danger" : "primary"} 
                                                                        className="text-capitalize"
                                                                    >
                                                                        {order?.orderStatus?.toLowerCase()}
                                                                    </Badge>
                                                                    
                                                                </div>
                                                                <div>
                                                                    <small className='me-1 text-dark fw-semibold'>
                                                                        {order?.outlet?.name}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                            <div className="fw-bold">₹{order.grandTotal?.toFixed(2)}</div>
                                                                <small className="text-muted">
                                                                    {order.orderItems.length} item{order.orderItems.length !== 1 ? 's' : ''}
                                                                </small>
                                                            </div>
                                                        </div>

                                                        <div className="order-items">
                                                            <h6 className="small text-muted mb-2">ITEMS</h6>
                                                            <ul className="list-unstyled mb-0">
                                                                {order?.orderItems?.map(item => (
                                                                    <li key={item._id} className="mb-2 pb-2 border-bottom">
                                                                    <div className="d-flex justify-content-between">
                                                                        <div>
                                                                        <div className="fw-medium">{item.productName}</div>
                                                                        {item.addons?.length > 0 && (
                                                                            <div className="mt-1">
                                                                            <small className="text-muted d-block">
                                                                                <strong>Addons:</strong> {item.addons.map(a => a.addonValueName).join(', ')}
                                                                            </small>
                                                                            </div>
                                                                        )}
                                                                        </div>
                                                                        <div className="text-end">
                                                                        <div>{item.quantity} × ₹{item.rate?.toFixed(2)}</div>
                                                                        <div className="fw-bold">₹{(item.quantity * item.rate)?.toFixed(2)}</div>
                                                                        </div>
                                                                    </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                        </div>
                                                    </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                                                        <Funnel size={28} className="text-muted" />
                                                    </div>
                                                    <h5 className="mb-2">No orders found</h5>
                                                    <p className="text-muted">
                                                        This subscription doesn't have any orders yet.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                )}
                            </Modal.Body>
                            <Modal.Footer className="border-top-0">
                                <Button variant="outline-secondary" onClick={() => setShowDetailsModal(false)}>
                                Close
                                </Button>
                            </Modal.Footer>
                            </Modal>
                        )
                    }

                    {/* Subscription Type Distribution */}
                    {/* {!loading && subscriptionPurchaseList?.length > 0 && (
                        <Row className="mt-4 g-3 pb-3">
                            <Col md={6}>
                                <Card className="h-100 shadow-sm">
                                    <Card.Header className="bg-white border-0 py-3">
                                        <h5 className="mb-0">SUBSCRIPTION TYPES</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Stack gap={3}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <Clock size={16} className="me-2 text-info" />
                                                    <span>Hourly</span>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-4">{stats.hourly}</span>
                                                    <span className="text-muted">{Math.round((stats.hourly / stats.total) * 100)}%</span>
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <Calendar size={16} className="me-2 text-primary" />
                                                    <span>Daily</span>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-4">{stats.daily}</span>
                                                    <span className="text-muted">{Math.round((stats.daily / stats.total) * 100)}%</span>
                                                </div>
                                            </div>
                                        </Stack>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className="h-100  shadow-sm">
                                    <Card.Header className="bg-white border-0 py-3">
                                        <h5 className="mb-0">STATUS DISTRIBUTION</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="d-flex flex-column gap-3">
                                            <div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small">Active</span>
                                                    <span className="small">{stats.active} ({Math.round((stats.active / stats.total) * 100)}%)</span>
                                                </div>
                                                <ProgressBar 
                                                    now={(stats.active / stats.total) * 100}
                                                    variant="success"
                                                    className="rounded-pill" 
                                                    style={{ height: '6px' }}
                                                />
                                            </div>
                                            <div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span className="small">Expired</span>
                                                    <span className="small">{stats.expired} ({Math.round((stats.expired / stats.total) * 100)}%)</span>
                                                </div>
                                                <ProgressBar 
                                                    now={(stats.expired / stats.total) * 100}
                                                    variant="danger"
                                                    className="rounded-pill" 
                                                    style={{ height: '6px' }}
                                                />
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )} */}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SubscriptionUsers;